// SYNC: near-identical across cres-t1/t4/t5/t6 (only the 401-refresh authenticate path differs per
// app) — keep in sync. The assistant backend is centralized in cres-aws-t2.
import axios from "axios";
import * as Analytics from "./Analytics";
import {SessionContext} from "./SessionContext";

// Cross-app axios instance for calling the Developer (t2) backend from the Foundation (t1) app.
// Mirrors AxiosRestAPI (the t3 cross-app instance): same interceptors, but pointed at the Developer
// API host. We present THIS app's t1 access token — t2's API-Gateway authorizer already accepts a
// `t1.user.access` token and the platform DB is shared, so the AI assistant running in the t2 Lambda
// operates on the same data. Used by src/data/assistant.ts to reach t2/v1/assistant.
// A 401 THAT MEANS "THAT PASSWORD IS WRONG" IS NOT AN EXPIRED SESSION, and must not be replayed.
//
// The handler below refreshes the token and retries on every 401. For a re-authentication refusal
// that retry submits the same wrong password a second time, and each attempt counts against the
// account's five-strike lockout -- so one click costs two strikes and a person gets two and a half
// tries before they are locked out for an hour. Measured in prod: one Record click left the
// counter at 2.
//
// A DENY LIST, NOT AN ALLOW LIST. An unknown 401 still refreshes and replays exactly as before, so
// session renewal cannot regress on a code nobody thought of. These four are the ones that mean
// the credential just typed was wrong, missing, or already locked out.
const REAUTHENTICATION_REFUSALS = ["CC-OAPR-PWBD", "CC-OAPR-PWMS", "CC-OAPR-PWNO", "CC-OAPR-PWLK"];

function isAReauthenticationRefusal(response: any): boolean {
    const code = response?.data?.Code;
    return typeof code === "string" && REAUTHENTICATION_REFUSALS.includes(code);
}

export function makeAxiosDeveloperAPI(sessionContext: any) {

    // may be a dict, null or a SessionContext object depending on login completion status
    if (!sessionContext) sessionContext = new SessionContext();

    const instance = axios.create({
        baseURL: process.env.REACT_APP_DEVELOPER_API_URL,
    });

    instance.interceptors.request.use(
        (config: any) => {
            if (config.metadata === undefined) {
                config.metadata = {};
                config.metadata.startTime = null;
                config.metadata.endTime = null;
            }
            config.metadata.startTime = new Date();

            Analytics.record("API Request", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
            });

            const token = sessionContext.getAccessToken();
            if (token) config.headers["X-Cres-AccessToken"] = token;

            return config;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
        (response: any) => {

            // Sometime response.data is string instead of object #1723
            // https://github.com/axios/axios/issues/1723
            if (response.headers["content-type"]?.includes("application/json")) {
                if (typeof response.data === "string" && response.data !== "") {
                    try {
                        response.data = JSON.parse(
                            response.data
                                .replace(/\\/g, "\\\\")
                                .replace(/\n/g, "\\n")
                                .replace(/\r/g, "\\r")
                                .replace(/\\"/g, "\""),
                        );
                    } catch (e) {
                        console.error(e);
                    }
                }
            }

            const config = response.config;
            config.metadata.endTime = new Date();

            Analytics.record("API Response", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
                "HTTP Code": response.status,
                "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                "Reason Code": response.data ? response.data.Code : undefined,
            });

            return response;
        },
        (error) => {

            const config = error.config;
            const response = error.response;

            config.metadata.endTime = new Date();

            // response can be undefined when there is a network issue / no connection
            if (response === undefined) {
                return Promise.reject(error);
            }

            Analytics.record("API Response", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
                "HTTP Code": response.status,
                "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                "Reason Code": response.data ? response.data.Code : undefined,
            });

            // On a 401, refresh THIS app's (t1) token via t1's own authenticate, then retry against t2.
            if (config.metadata.disableRefresh !== true) {
                if (config.metadata.cogntioRefreshed === undefined
                    && !isAReauthenticationRefusal(response)) {
                    config.metadata.cogntioRefreshed = true;

                    if (response.status === 401) {
                        return axios({
                            method: "PUT",
                            url: `${process.env.REACT_APP_API_URL}/t1/v1/authenticate`,
                            data: {
                                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
                            },
                        }).then((response) => {
                            const token = response.data.AccessToken;
                            sessionContext.setAccessToken(token);
                            config.headers["X-Cres-AccessToken"] = token;
                            // Retry through `instance`, NOT raw axios: the response interceptor must run on the retry
                            // too, or its JSON-string repair is skipped and callers silently receive an unparsed body.
                            // No loop risk: cogntioRefreshed is already set, so a second 401 falls through to reject.
                            return instance(config);
                        });
                    }
                }
            }

            return Promise.reject(error);
        },
    );

    return instance;
}

const AxiosDeveloperAPI = makeAxiosDeveloperAPI(null);

export default AxiosDeveloperAPI;
